<template>
  <div class="pd-select pd-supplier" :class="{ 'pd-select--open': isOpen }" ref="selectWrapper">
    <div class="pd-select__control" @click="isOpen = !isOpen">
      <span class="pd-select__value" :class="{ 'pd-select__placeholder': !value }">
        {{ selectedLabel }}
      </span>
      <div class="pd-select__actions">
        <i v-if="value" class="fa-solid fa-xmark pd-select__clear" @click.stop="clearSelection"></i>
        <i class="fa-solid fa-chevron-down pd-select__arrow"></i>
      </div>
    </div>
    
    <transition name="pd-fade-slide">
      <div v-if="isOpen" class="pd-select__menu">
        <div class="pd-select__menu-inner">
          <div class="pd-select__search">
            <i class="fa-solid fa-magnifying-glass"></i>
            <input 
              type="text" 
              v-model="searchQuery" 
              placeholder="Buscar proveedor..." 
              @click.stop
              ref="searchInput"
            />
          </div>

          <div v-if="isLoading" class="pd-select__empty">
            <i class="fa-solid fa-circle-notch fa-spin"></i> Buscando...
          </div>

          <template v-else>
            <div 
              v-for="opt in options" 
              :key="opt.supID || opt.SupID"
              class="pd-select__option"
              :class="{ 'pd-select__option--selected': isSelected(opt.SupCode || opt.supCode || opt.SupID) }"
              @click="selectOption(opt.SupCode || opt.supCode || opt.SupID, opt.SupName || opt.supName)"
            >
              {{ opt.SupName || opt.supName }}
              <i v-if="isSelected(opt.SupCode || opt.supCode || opt.SupID)" class="fa-solid fa-check pd-select__check"></i>
            </div>

            <div v-if="options.length === 0 && searchQuery" class="pd-select__empty">
              Sin resultados
            </div>
          </template>
        </div>
      </div>
    </transition>
  </div>
</template>

<script>
import _sSupplier from "@/services/Logistics/SupplierService";

export default {
  name: 'DashboardSupplier',
  props: {
    value: { type: [String, Number], default: null },
    multiple: { type: Boolean, default: false }
  },
  data() {
    return {
      isOpen: false,
      searchQuery: '',
      options: [],
      isLoading: false,
      searchTimeout: null,
      selectedName: null,
    };
  },
  computed: {
    selectedValuesArray() {
      if (this.value === null || this.value === undefined || this.value === '') return [];
      return String(this.value).split(',').filter(Boolean);
    },
    selectedLabel() {
      if (this.multiple) {
        if (!this.value) return 'Todos los Proveedores';
        const arr = this.selectedValuesArray;
        if (arr.length === 0) return 'Todos los Proveedores';
        if (arr.length === 1) return this.selectedName || arr[0];
        return `${arr.length} seleccionados`;
      } else {
        if (!this.value) return 'Todos los Proveedores';
        return this.selectedName || this.value;
      }
    }
  },
  watch: {
    isOpen(val) {
      if (val) {
        this.searchQuery = '';
        this.fetchSuppliers('');
        this.$nextTick(() => {
          if (this.$refs.searchInput) this.$refs.searchInput.focus();
        });
      }
    },
    searchQuery(val) {
      if (this.searchTimeout) clearTimeout(this.searchTimeout);
      this.searchTimeout = setTimeout(() => {
        this.fetchSuppliers(val);
      }, 400); // debounce de 400ms para no saturar el SP
    }
  },
  mounted() {
    document.addEventListener('click', this.handleClickOutside);
  },
  beforeDestroy() {
    document.removeEventListener('click', this.handleClickOutside);
    if (this.searchTimeout) clearTimeout(this.searchTimeout);
  },
  methods: {
    async fetchSuppliers(search) {
      this.isLoading = true;
      try {
        const url = _sSupplier.getSupplierSearch();
        const requestID = this.$fun && this.$fun.getUserID ? this.$fun.getUserID() : 0;
        
        // El API /Suppliersearch hace match y devuelve un arreglo de {SupID, SupName, etc}
        const res = await this.$http.post(url, {}, {
          params: { search: search || '', requestID }
        });
        
        if (res && res.data) {
          this.options = res.data;
        }
      } catch (err) {
        console.error("Error obteniendo proveedores:", err);
        this.options = [];
      } finally {
        this.isLoading = false;
      }
    },
    isSelected(val) {
      if (this.multiple) {
        return this.selectedValuesArray.includes(String(val));
      }
      return String(val) === String(this.value);
    },
    selectOption(val, label) {
      if (this.multiple) {
        let arr = [...this.selectedValuesArray];
        const strVal = String(val);
        const idx = arr.indexOf(strVal);
        if (idx > -1) {
          arr.splice(idx, 1);
        } else {
          arr.push(strVal);
        }
        const newVal = arr.length > 0 ? arr.join(',') : null;
        if (arr.length === 1) this.selectedName = label;
        this.$emit('input', newVal);
        this.$emit('change', newVal);
      } else {
        this.selectedName = label;
        this.$emit('input', val);
        this.$emit('change', val);
        this.isOpen = false;
      }
    },
    clearSelection() {
      this.selectedName = null;
      this.$emit('input', null);
      this.$emit('change', null);
      this.isOpen = false;
    },
    handleClickOutside(e) {
      if (this.$refs.selectWrapper && !this.$refs.selectWrapper.contains(e.target)) {
        this.isOpen = false;
      }
    }
  }
};
</script>

<style scoped>
/* Reutilizamos los tokens de diseño del DashboardSelect */
.pd-select {
  position: relative !important;
  width: 100% !important;
  font-family: 'Inter', sans-serif !important;
}
.pd-select__control {
  display: flex !important;
  align-items: center !important;
  justify-content: space-between !important;
  width: 100% !important;
  padding: 8px 12px !important;
  background: #fff !important;
  border: 1px solid #E2E8F0 !important;
  border-radius: 8px !important;
  cursor: pointer !important;
  font-size: 11px !important;
  color: #334155 !important;
  transition: all 0.2s ease !important;
}
.pd-select:hover .pd-select__control {
  border-color: #CBD5E1 !important;
}
.pd-select--open .pd-select__control {
  border-color: #0A5C36 !important;
  box-shadow: 0 0 0 3px rgba(10,92,54,0.1) !important;
}
.pd-select__value {
  white-space: nowrap !important;
  overflow: hidden !important;
  text-overflow: ellipsis !important;
  font-weight: 500 !important;
}
.pd-select__placeholder {
  color: #94A3B8 !important;
  font-weight: 400 !important;
}
.pd-select__actions {
  display: flex !important;
  align-items: center !important;
  gap: 6px !important;
}
.pd-select__arrow, .pd-select__clear {
  font-size: 10px !important;
  color: #94A3B8 !important;
  transition: transform 0.2s ease, color 0.2s ease !important;
}
.pd-select__clear {
  cursor: pointer !important;
}
.pd-select__clear:hover {
  color: #EF4444 !important;
}
.pd-select--open .pd-select__arrow {
  transform: rotate(180deg) !important;
  color: #0A5C36 !important;
}

.pd-select__menu {
  position: absolute !important;
  top: calc(100% + 4px) !important;
  left: 0 !important;
  width: 100% !important;
  background: #fff !important;
  border: 1px solid #E2E8F0 !important;
  border-radius: 8px !important;
  box-shadow: 0 4px 12px rgba(0,0,0,0.08) !important;
  z-index: 100 !important;
  max-height: 250px !important;
  overflow-y: auto !important;
}
.pd-select__menu::-webkit-scrollbar { width: 4px !important; }
.pd-select__menu::-webkit-scrollbar-track { background: transparent !important; }
.pd-select__menu::-webkit-scrollbar-thumb { background: #CBD5E1 !important; border-radius: 4px !important; }

.pd-select__menu-inner {
  padding: 4px !important;
  display: flex !important;
  flex-direction: column !important;
  gap: 2px !important;
}
.pd-select__search {
  display: flex !important;
  align-items: center !important;
  padding: 6px 10px !important;
  gap: 8px !important;
  border-bottom: 1px solid #E2E8F0 !important;
  margin-bottom: 4px !important;
  color: #94A3B8 !important;
}
.pd-select__search input {
  border: none !important;
  outline: none !important;
  width: 100% !important;
  font-size: 11px !important;
  color: #334155 !important;
  background: transparent !important;
}
.pd-select__empty {
  padding: 10px !important;
  text-align: center !important;
  font-size: 11px !important;
  color: #94A3B8 !important;
  font-style: italic !important;
}

.pd-select__option {
  padding: 8px 10px !important;
  font-size: 11px !important;
  color: #475569 !important;
  border-radius: 6px !important;
  cursor: pointer !important;
  display: flex !important;
  align-items: center !important;
  justify-content: space-between !important;
  transition: background 0.15s !important;
}
.pd-select__option:hover {
  background: #F1F5F9 !important;
  color: #1E293B !important;
}
.pd-select__option--selected {
  background: #ECFDF5 !important;
  color: #059669 !important;
  font-weight: 600 !important;
}
.pd-select__check {
  font-size: 10px !important;
}

/* Animación */
.pd-fade-slide-enter-active, .pd-fade-slide-leave-active {
  transition: all 0.2s ease !important;
}
.pd-fade-slide-enter, .pd-fade-slide-leave-to {
  opacity: 0 !important;
  transform: translateY(-5px) !important;
}
</style>
